import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { keys } from '../../api/keys'
import { runBulkEdit, type BulkKind } from './bulkEdit'
import type { BulkOutcome } from './runBulk'
import { useSelection } from './selection'
import type { Txn } from './transactionWire'

export type { BulkKind }

type Options = {
  /** One call per operation: `{action_kind, item_count, error_count}`, counts and an enum only. */
  onPerformed?: (e: { action_kind: BulkKind; item_count: number; error_count: number }) => void
}

/**
 * Run one bulk edit over the selected rows (M6-R9).
 *
 * The outcome is per row. After it settles the transactions list is
 * invalidated — a stale list, and an Overview behind it, is the classic miss.
 * The selection is then reduced to the failed rows **only if no filter
 * change happened since the run started** — compared by generation, a counter
 * that never repeats, so filters A→B→A mid-run do not pass for "unchanged". If
 * the user changed filters mid-run the selection was already cleared and may
 * since hold new rows, which a late result must neither wipe nor add to (Review
 * Focus 3). The run itself always finishes and always reports.
 */
export function useBulkActions({ onPerformed }: Options = {}) {
  const client = useQueryClient()
  const { generation, dispatch } = useSelection()
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)

  const latestGeneration = useRef(generation)
  const performed = useRef(onPerformed)
  useEffect(() => {
    latestGeneration.current = generation
    performed.current = onPerformed
  })

  const run = useCallback(
    async (kind: BulkKind, rows: readonly Txn[], value: string | null): Promise<BulkOutcome> => {
      const startedUnder = latestGeneration.current
      const { done, failed, skipped } = await runBulkEdit(kind, rows, value, (d, total) => setProgress({ done: d, total }))
      setProgress(null)

      performed.current?.({ action_kind: kind, item_count: rows.length, error_count: failed.length })
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.transactions.all() }),
        // A category or merchant move changes Cashflow's breakdowns (and its "uncategorised" / "no merchant"
        // remainders) as the single-row quick sets do; a tag or an owner is no cashflow figure.
        ...(kind === 'set_category' || kind === 'set_merchant' ? [client.invalidateQueries({ queryKey: keys.cashflow.all() })] : []),
      ])

      if (latestGeneration.current === startedUnder) {
        dispatch({ t: 'retain', ids: failed.map((f) => f.id) })
      }
      return { done, failed, skipped }
    },
    [client, dispatch],
  )

  return { run, progress }
}
